import {
    Button,
    checkPhoneLengthFromMask,
    getMaskFromName,
    Input,
    onlyLettersRule,
    Pending,
    PopoverInput,
    requiredRule,
    toInt,
    unmaskNumberString
} from '@finbeat/react-toolkit';
import ClientChangeModal from 'components/Workplaces/Clients/ClientChangeModal';
import ClientWithdrawMoneyModal from 'components/Workplaces/Clients/ClientWithdrawMoney/ClientWithdrawMoneyModal';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { useClientInfoTabContext } from 'providers/ClientInfoTabProvider';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { RefObject, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AccordionBody, AccordionHeader, AccordionItem, Col, FormGroup, Row } from 'reactstrap';
import { getMaskedCard } from 'utils/masks/cardMasks';
import { docMask } from 'utils/masks/docMasks';
import GetOptions from 'utils/options';
import EditButtonPopover from '../Additions/EditButtonPopover';
import { ClientTabEditedFieldProps } from '../Tabs/ClientTab';

type AddressProps = {
    country: string;
    region: string;
    district: string;
};

const ClientTabPersonalInfo = ({
    modalRef,
    isEditable,
}: {
    modalRef?: RefObject<HTMLElement>;
    isEditable: boolean;
}) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();

    const [clientOpen, setClientOpen] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<string | number>();
    const [selectedCardNumber, setSelectedCardNumber] = useState<string>('');

    const {
        clientInfo,
        loading,
        dictionaries,
        dictLoading,
        editingReason,
        changeInputField,
        orderId,
    } = useClientInfoTabContext();

    const { dateFormat } = useGeneralSettingsContext();

    const address = useMemo(() => clientInfo?.addresses && clientInfo?.addresses[0], [clientInfo]);

    const { addressProps } = useMemo(() => {
        const common = { reason: editingReason, dsc: '' };
        return {
            addressProps: {
                ...common,
                input: address?.fullAddress ?? '',
                country: address?.countryId?.toString() ?? '',
                region: address?.regionId?.toString() ?? '',
                district: address?.districtId?.toString() ?? '',
            },
            typeOfAccommodation: {
                input: clientInfo?.moreData['TypeOfAccommodation'] ?? '',
                ...common,
            },
            timeOfCurrentResidence: {
                input: clientInfo?.moreData['TimeOfCurrentResidence'] ?? '',
                ...common,
            },
            maritalStatus: { input: clientInfo?.moreData['MaritalStatus'] ?? '', ...common },
        };
    }, [address, clientInfo, editingReason]);

    const {
        pinfl,
        phoneNumber,
        collectionStatus,
        additionalPhoneNumber,
        docNum,
        firstName,
        middleName,
        lastName,
        sex,
        birthday,
        docDt,
    } = useMemo(() => {
        const common = { reason: editingReason, dsc: '' };

        return {
            collectionStatus: { input: clientInfo?.collectionStatus ?? '', ...common },
            phoneNumber: { input: clientInfo?.phoneNumber ?? '', ...common },
            additionalPhoneNumber: {
                input: clientInfo?.moreData['AdditionalPhoneNumber'] ?? '',
                ...common,
            },
            docNum: {
                input: `${clientInfo?.docSeries ?? ''} ${clientInfo?.docNum ?? ''}`,
                ...common,
            },
            docDt: { input: clientInfo?.docDt ?? clientInfo?.docDt ?? null, ...common },
            firstName: { input: clientInfo?.firstName ?? '', ...common },
            middleName: { input: clientInfo?.middleName ?? '', ...common },
            lastName: { input: clientInfo?.lastName ?? '', ...common },
            sex: { input: clientInfo?.sex ?? '', ...common },
            birthday: { input: clientInfo?.birthday ?? null, ...common },
            pinfl: { input: clientInfo?.moreData['Pinfl'] ?? '', ...common },
        };
    }, [clientInfo, editingReason]);

    const renderSaveChangesPopover = useCallback(
        () => <Col>
            <h5>
                <Trans i18nKey="BackOfficeSaveChanges" />?
            </h5>
        </Col>,
        []
    );

    return (
        <>
            <AccordionItem>
                <AccordionHeader targetId="personal">{t('BackOfficePersonalInfo')}</AccordionHeader>
                <AccordionBody accordionId="personal">
                    <Row>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                label={t('BackOfficePinfl')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'Pinfl',
                                        value: values.input,
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={pinfl}
                                rules={{
                                    required: requiredRule,
                                }}
                            />
                        </Col>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                maskProps={{
                                    mask: getMaskFromName('Uzbekistan'),
                                }}
                                label={t('BackOfficePhone')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'PhoneNumber',
                                        value: unmaskNumberString(values.input),
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={phoneNumber}
                                rules={{
                                    required: requiredRule,
                                    validate: (value) => checkPhoneLengthFromMask(value),
                                }}
                            />
                        </Col>
                        {(loading || clientInfo?.hasActiveLoans) && (
                            <Col>
                                <PopoverInput<ClientTabEditedFieldProps<string>>
                                    name="input"
                                    label={t('BackOfficeCollectionStatus')}
                                    type="select"
                                    loading={loading || dictLoading}
                                    options={GetOptions(dictionaries.collectionStatus, true)}
                                    mutationFunction={({ values }) =>
                                        changeInputField({
                                            type: 'CollectionStatus',
                                            value: values.input,
                                            reason: values.reason,
                                            dsc: values.dsc,
                                        })
                                    }
                                    popoverContainer={modalRef}
                                    disabled={
                                        !isEditable ||
                                        !hasRole('clientsUpdateCollectionStatus') ||
                                        clientInfo?.collectionStatus === 'GoodStanding'
                                    }
                                    editButtonPopover={renderSaveChangesPopover}
                                    defaultValues={collectionStatus}
                                />
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                label={t('BackOfficeDocNum')}
                                loading={loading}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        docInfo: {
                                            docSeries: values.input.slice(0, 2),
                                            docNum: unmaskNumberString(values.input),
                                            docType: clientInfo?.docType ?? '',
                                            docDt: clientInfo?.docDt ?? null,
                                            docOrgCode: clientInfo?.docOrgCode ?? '',
                                            docOrgName: clientInfo?.docOrgName ?? '',
                                        },
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={docNum}
                                maskProps={{
                                    mask: docMask,
                                }}
                                rules={{
                                    required: requiredRule,
                                    validate: (value) => {
                                        if (value < 9) return 'BackOfficeInvalidFieldValue';
                                    },
                                }}
                            />
                        </Col>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                maskProps={{
                                    mask: getMaskFromName('Uzbekistan'),
                                }}
                                label={t('BackOfficeAdditionalPhoneNumber')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'AdditionalPhoneNumber',
                                        value: unmaskNumberString(values.input),
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={additionalPhoneNumber}
                                rules={{
                                    required: requiredRule,
                                    validate: (value) => checkPhoneLengthFromMask(value),
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="3">
                            <PopoverInput<ClientTabEditedFieldProps<string | null>>
                                dateTimeProps={{ dateOnly: true, displayDateFormat: dateFormat }}
                                name="input"
                                label={t('BackOfficeDocumentIssuedDate')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        docInfo: {
                                            docDt: format(
                                                values.input ? new Date(values.input) : new Date(),
                                                dateFormat
                                            ),
                                            docSeries: clientInfo?.docSeries ?? '',
                                            docNum: clientInfo?.docNum ?? '',
                                            docType: clientInfo?.docType ?? '',
                                            docOrgCode: clientInfo?.docOrgCode ?? '',
                                            docOrgName: clientInfo?.docOrgName ?? '',
                                        },
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={docDt}
                            />
                        </Col>
                        <Col>
                            <Input
                                name="issuedBy"
                                value={`${clientInfo?.docOrgCode ?? ''} ${clientInfo?.docOrgName ?? ''
                                    }`}
                                disabled
                                loading={loading}
                                label={t('BackOfficeIssuedBy')}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                label={t('BackOfficeFirstName')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'FirstName',
                                        value: values.input,
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={firstName}
                                rules={{
                                    required: requiredRule,
                                    pattern: onlyLettersRule,
                                }}
                            />
                        </Col>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                label={t('BackOfficeMiddleName')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'MiddleName',
                                        value: values.input,
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={middleName}
                                rules={{
                                    required: requiredRule,
                                    pattern: onlyLettersRule,
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                label={t('BackOfficeLastName')}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'LastName',
                                        value: values.input,
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                loading={loading}
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={lastName}
                                rules={{
                                    required: requiredRule,
                                    pattern: onlyLettersRule,
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="3">
                            <FormGroup>
                                <PopoverInput<ClientTabEditedFieldProps<string | null>>
                                    dateTimeProps={{
                                        dateOnly: true,
                                        displayDateFormat: dateFormat,
                                    }}
                                    name="input"
                                    label={t('BackOfficeBirthday')}
                                    mutationFunction={({ values }) =>
                                        changeInputField({
                                            type: 'Birthday',
                                            value: format(
                                                values.input ? new Date(values.input) : new Date(),
                                                dateFormat
                                            ),
                                            reason: values.reason,
                                            dsc: values.dsc,
                                        })
                                    }
                                    loading={loading}
                                    popoverContainer={modalRef}
                                    disabled={!isEditable}
                                    editButtonPopover={EditButtonPopover}
                                    defaultValues={birthday}
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string>>
                                name="input"
                                label={t('BackOfficeSex')}
                                type="select"
                                loading={loading || dictLoading}
                                options={GetOptions(dictionaries.sex, true)}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        type: 'Sex',
                                        value: values.input,
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                popoverContainer={modalRef}
                                disabled={!isEditable}
                                editButtonPopover={EditButtonPopover}
                                defaultValues={sex}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PopoverInput<ClientTabEditedFieldProps<string> & AddressProps>
                                name="input"
                                label={t('BackOfficeRegistrationAddress')}
                                loading={loading}
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        addressInfo: {
                                            countryId: toInt(values.country) ?? 0,
                                            regionId: toInt(values.region) ?? 0,
                                            districtId: toInt(values.district) ?? 0,
                                            address: values.input,
                                        },
                                        reason: values.reason,
                                        dsc: values.dsc,
                                    })
                                }
                                popoverContainer={modalRef}
                                disabled
                                editButtonPopover={EditButtonPopover}
                                defaultValues={addressProps}
                                rules={{
                                    required: requiredRule,
                                }}
                                className="client-info__address-info"
                            />
                        </Col>
                    </Row>
                    {orderId && (loading || clientInfo?.bankDetails.find((b) => b.isMain)) && (
                        <Row>
                            <Col>
                                <Input
                                    name="clientCardNumber"
                                    value={getMaskedCard(
                                        clientInfo?.bankDetails.find((b) => b.isMain)?.accountNumber
                                    )}
                                    disabled
                                    loading={loading}
                                    label={t('BackOfficeCardNumber')}
                                />
                            </Col>
                        </Row>
                    )}
                </AccordionBody>
            </AccordionItem>
            {!orderId && (loading || clientInfo?.bankDetails) && (
                <AccordionItem>
                    <AccordionHeader targetId="clientCards">
                        {t('BackOfficeBankCards')}
                    </AccordionHeader>
                    <AccordionBody accordionId="clientCards">
                        {loading ? (
                            <Pending />
                        ) : (
                            clientInfo?.bankDetails
                                .sort((b) => (b.isMain ? -1 : 1))
                                .map((b) => (
                                    <div key={b.bankId} className="input-group">
                                        <div className="input-group-text mb-3">
                                            <Input
                                                name="clientCardIsMain"
                                                type="radio"
                                                checked={b.isMain}
                                                disabled
                                                loading={loading}
                                            />
                                        </div>
                                        <Input
                                            name="clientCardNumber"
                                            value={getMaskedCard(b.accountNumber)}
                                            disabled
                                            loading={loading}
                                            label={t('BackOfficeCardNumber')}
                                        />
                                        {hasRole('clientManualPayment') &&
                                            clientInfo.hasActiveLoans && (
                                                <Button
                                                    color="primary mb-3"
                                                    onClick={() => {
                                                        setWithdrawOpen(true);
                                                        setSelectedCard(b.id);
                                                        setSelectedCardNumber(
                                                            getMaskedCard(b.accountNumber)
                                                        );
                                                    }}
                                                >
                                                    <Trans i18nKey="BackOfficeWithdrawMoney" />
                                                </Button>
                                            )}
                                    </div>
                                ))
                        )}
                    </AccordionBody>
                </AccordionItem>
            )}
            {clientOpen && (
                <ClientChangeModal isOpen={clientOpen} toggle={setClientOpen} id={clientInfo?.id} />
            )}
            {clientInfo?.hasActiveLoans && hasRole('clientManualPayment') && withdrawOpen && (
                <ClientWithdrawMoneyModal
                    isOpen={withdrawOpen}
                    toggle={setWithdrawOpen}
                    clientId={clientInfo?.id ?? 0}
                    cardId={selectedCard ?? ''}
                    cardNumber={selectedCardNumber}
                />
            )}
        </>
    );
};

export default ClientTabPersonalInfo;
